import React, { useState } from 'react';

interface Quest {
  id: string;
  title: string;
  description: string;
  xp: number;
  completed: boolean;
  unlocked: boolean;
}

interface Achievement {
  id: string;
  name: string;
  icon: string;
  unlocked: boolean;
}

export const SESQuest: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [xp, setXp] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  
  const [quests, setQuests] = useState<Quest[]>([
    {
      id: 'iam-palace',
      title: '🏰 IAM Permission Palace',
      description: 'Create an IAM user with the perfect permissions',
      xp: 100,
      completed: false,
      unlocked: true
    },
    {
      id: 'domain-dragon',
      title: '🐉 Domain Verification Dragon',
      description: 'Slay the DNS records and verify your domain',
      xp: 200,
      completed: false,
      unlocked: false
    },
    {
      id: 'sandbox-escape',
      title: '🏖️ Sandbox Escape Room',
      description: 'Break free from the testing sandbox',
      xp: 300,
      completed: false,
      unlocked: false
    },
    {
      id: 'weapon-choice',
      title: '⚔️ SMTP vs API Duel',
      description: 'Choose your weapon wisely',
      xp: 150,
      completed: false,
      unlocked: false
    },
    {
      id: 'bounce-boss',
      title: '🛡️ Bounce Handler Boss',
      description: 'Defeat spam filters with SNS topics',
      xp: 250,
      completed: false,
      unlocked: false
    }
  ]);

  const [achievements, setAchievements] = useState<Achievement[]>([
    { id: 'first-steps', name: 'First Steps', icon: '👣', unlocked: false },
    { id: 'dns-master', name: 'DNS Master', icon: '🧙‍♂️', unlocked: false },
    { id: 'email-hero', name: 'Email Hero', icon: '📧', unlocked: false },
    { id: 'speed-demon', name: 'Speed Demon', icon: '⚡', unlocked: false },
  ]);

  const [currentQuest, setCurrentQuest] = useState<string | null>(null);
  const [questProgress, setQuestProgress] = useState(0);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [emailDetails, setEmailDetails] = useState({ to: '', subject: '', message: '' });

  const startGame = () => {
    if (playerName.trim()) {
      setGameStarted(true);
      unlockAchievement('first-steps');
    }
  };

  const unlockAchievement = (id: string) => {
    setAchievements(prev => 
      prev.map(a => a.id === id ? { ...a, unlocked: true } : a)
    );
  };

  const startQuest = (questId: string) => {
    setCurrentQuest(questId);
    setQuestProgress(0);
  };

  const completeQuest = (questId: string) => {
    const quest = quests.find(q => q.id === questId);
    if (!quest) return;

    setQuests(prev => {
      const updated = prev.map(q => {
        if (q.id === questId) {
          return { ...q, completed: true };
        }
        // Unlock next quest
        const currentIndex = prev.findIndex(q => q.id === questId);
        if (prev[currentIndex + 1] && prev[currentIndex + 1].id === q.id) {
          return { ...q, unlocked: true };
        }
        return q;
      });
      return updated;
    });

    setXp(prev => prev + quest.xp);
    setCurrentQuest(null);
    
    // Level up logic
    if (xp + quest.xp >= currentLevel * 500) {
      setCurrentLevel(prev => prev + 1);
    }

    // Special achievements
    if (questId === 'domain-dragon') {
      unlockAchievement('dns-master');
    }
  };

  if (!gameStarted) {
    return (
      <div className="bg-gray-900 p-8 rounded-lg text-white border border-gray-700">
        <h1 className="text-4xl font-bold mb-4 text-center">🎮 AWS SES Quest</h1>
        <p className="text-center mb-6">Turn email setup into an adventure!</p>
        
        <div className="max-w-md mx-auto">
          <input
            type="text"
            placeholder="Enter your hero name..."
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            className="w-full px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-white/50 mb-4"
            onKeyPress={(e) => e.key === 'Enter' && startGame()}
          />
          <button
            onClick={startGame}
            disabled={!playerName.trim()}
            className="w-full bg-green-600 py-3 rounded font-bold hover:bg-green-700 transition-all disabled:opacity-50"
          >
            Begin Your Quest
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 p-8 rounded-lg text-white border border-gray-700">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">🎮 {playerName}</h2>
          <p className="text-sm opacity-75">Level {currentLevel} Email Wizard</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold">{xp} XP</p>
          <div className="w-32 bg-white/20 rounded-full h-2 mt-1">
            <div 
              className="bg-yellow-400 h-full rounded-full transition-all"
              style={{ width: `${(xp % 500) / 5}%` }}
            />
          </div>
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="relative mb-8">
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-700"></div>
        <div 
          className="absolute top-6 left-0 h-0.5 bg-green-500 transition-all duration-500"
          style={{ width: `${(quests.filter(q => q.completed).length / quests.length) * 100}%` }}
        ></div>
        <div className="relative flex justify-between">
          {quests.map((quest, idx) => (
            <div 
              key={quest.id} 
              className={`flex flex-col items-center cursor-pointer transition-all ${
                quest.unlocked && !quest.completed ? 'hover:scale-110' : ''
              }`}
              onClick={() => quest.unlocked && !quest.completed && startQuest(quest.id)}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl border-2 transition-all ${
                quest.completed 
                  ? 'bg-green-600 border-green-500 shadow-lg shadow-green-500/30' 
                  : quest.unlocked 
                  ? 'bg-gray-800 border-green-500 hover:border-green-400 shadow-md' 
                  : 'bg-gray-800 border-gray-700'
              }`}>
                {quest.completed ? '✓' : quest.title.split(' ')[0]}
              </div>
              <span className="text-xs mt-2 max-w-[100px] text-center font-medium">
                {quest.title.split(' ').slice(1).join(' ')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-2">🏆 Achievements</h3>
        <div className="flex gap-2">
          {achievements.map(achievement => (
            <div
              key={achievement.id}
              className={`text-3xl p-2 rounded ${
                achievement.unlocked ? 'bg-white/20' : 'bg-white/5 opacity-50'
              }`}
              title={achievement.name}
            >
              {achievement.icon}
            </div>
          ))}
        </div>
      </div>

      {/* Quest List */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold mb-2">📜 Quests</h3>
        {quests.map(quest => (
          <div
            key={quest.id}
            className={`p-4 rounded-lg border-2 transition-all ${
              quest.completed
                ? 'bg-green-800/30 border-green-500'
                : quest.unlocked
                ? 'bg-white/10 border-white/30 hover:bg-white/20 cursor-pointer'
                : 'bg-white/5 border-white/10 opacity-50'
            }`}
            onClick={() => quest.unlocked && !quest.completed && startQuest(quest.id)}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-bold flex items-center gap-2">
                  {quest.title}
                  {quest.completed && <span className="text-green-400">✓</span>}
                </h4>
                <p className="text-sm opacity-75">{quest.description}</p>
              </div>
              <div className="text-right">
                <p className="font-bold">{quest.xp} XP</p>
                {!quest.unlocked && <p className="text-xs">🔒 Locked</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Current Quest Modal */}
      {currentQuest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-600 p-6 rounded-lg max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4">
              {quests.find(q => q.id === currentQuest)?.title}
            </h3>
            
            {/* Quest-specific content */}
            {currentQuest === 'iam-palace' && (
              <div className="bg-white/10 p-4 rounded mb-4 space-y-3">
                <p className="text-sm opacity-75">Let's test the email service!</p>
                <input
                  type="email"
                  placeholder="Recipient email"
                  value={emailDetails.to}
                  onChange={(e) => setEmailDetails({...emailDetails, to: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
                />
                <input
                  type="text"
                  placeholder="Subject"
                  value={emailDetails.subject}
                  onChange={(e) => setEmailDetails({...emailDetails, subject: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white"
                />
                <textarea
                  placeholder="Message"
                  value={emailDetails.message}
                  onChange={(e) => setEmailDetails({...emailDetails, message: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 rounded border border-white/20 text-white h-20"
                />
                <button
                  onClick={async () => {
                    if (emailDetails.to && emailDetails.subject) {
                      setEmailStatus('sending');
                      setQuestProgress(50);
                      try {
                        const response = await fetch('http://localhost:8789/send', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            to: emailDetails.to,
                            subject: emailDetails.subject,
                            text: emailDetails.message || 'Test email from SES Quest!'
                          })
                        });
                        const result = await response.json();
                        if (result.success) {
                          setEmailStatus('success');
                          setQuestProgress(100);
                          setTimeout(() => completeQuest(currentQuest), 1500);
                        } else {
                          setEmailStatus('error');
                        }
                      } catch (error) {
                        setEmailStatus('error');
                        console.error('Failed to send email:', error);
                      }
                    }
                  }}
                  disabled={!emailDetails.to || !emailDetails.subject || emailStatus === 'sending'}
                  className="w-full bg-blue-500 py-2 rounded font-bold hover:bg-blue-600 disabled:opacity-50"
                >
                  {emailStatus === 'sending' ? '📧 Sending...' :
                   emailStatus === 'success' ? '✅ Sent!' :
                   emailStatus === 'error' ? '❌ Failed - Try Again' :
                   '📧 Send Test Email'}
                </button>
              </div>
            )}

            {currentQuest !== 'iam-palace' && (
              <div className="bg-white/10 p-4 rounded mb-4">
                <p className="mb-2">Quest Progress</p>
                <div className="w-full bg-white/20 rounded-full h-4">
                  <div
                    className="bg-green-400 h-full rounded-full transition-all"
                    style={{ width: `${questProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {currentQuest !== 'iam-palace' && (
                <button
                  onClick={() => {
                    setQuestProgress(100);
                    setTimeout(() => completeQuest(currentQuest), 500);
                  }}
                  className="flex-1 bg-green-500 py-2 rounded font-bold hover:bg-green-600"
                >
                  Complete Quest (Demo)
                </button>
              )}
              <button
                onClick={() => {
                  setCurrentQuest(null);
                  setEmailStatus('idle');
                  setEmailDetails({ to: '', subject: '', message: '' });
                }}
                className="flex-1 bg-red-500 py-2 rounded font-bold hover:bg-red-600"
              >
                Abandon Quest
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};